import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { automationApi, type RunFilters } from '@/lib/automation-api';
import { getErrorMessage } from '@/lib/errors';
import { compactAgo } from '@/modules/backups/format';
import { countsSentence } from '@/modules/automation/status';
import { RunStatusGlyph } from '@/modules/automation/ui';

/** A short list of runs, for the side of a form or the dashboard. Polls while any is in progress. */
export function RecentRuns({
  queryKey,
  filters,
  empty = 'Nothing has run yet.',
}: {
  queryKey: readonly unknown[];
  filters: RunFilters;
  empty?: string;
}) {
  const runs = useQuery({
    queryKey,
    queryFn: () => automationApi.listRuns(filters),
    refetchInterval: (query) => (query.state.data?.items.some((run) => !run.is_finished) ? 3000 : false),
  });

  if (runs.isLoading) {
    return <p className="px-5 pb-5 text-sm text-muted-foreground">Loading…</p>;
  }

  if (runs.isError) {
    return <p className="px-5 pb-5 text-sm text-danger">{getErrorMessage(runs.error)}</p>;
  }

  const items = runs.data?.items ?? [];

  if (items.length === 0) {
    return <p className="px-5 pb-5 text-sm text-muted-foreground">{empty}</p>;
  }

  return (
    <ul className="divide-y divide-slate-900/[0.06] pb-2 dark:divide-white/[0.05]">
      {items.map((run) => (
        <li key={run.id}>
          <Link to={`/automation/runs/${run.id}`} className="flex items-center gap-3 px-5 py-2.5 transition-colors hover:bg-foreground/[0.03]">
            <RunStatusGlyph status={run.status} label={run.status_label} size="sm" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-medium">{run.title}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {run.targets_label} · {countsSentence(run)}
              </span>
            </span>
            <span className="shrink-0 text-xs text-muted-foreground">{compactAgo(run.created_at)}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
